import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, GraduationCap, ListChecks, Sparkles } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

export default function CheckupIntro({ pathway, onStart }) {
  const { intro } = pathway;
  const Icon = pathway.icon;
  const features = [
    { icon: Clock, text: intro.minutes },
    { icon: ListChecks, text: "Simple questions" },
    { icon: GraduationCap, text: "Educational guidance" },
    { icon: Sparkles, text: "Personalized results" },
  ];

  return (
    <motion.div key="intro" initial="hidden" animate="show" exit={{ opacity: 0, y: -16 }} variants={{ hidden: {}, show: { transition: { staggerChildren: 0.09 } } }} data-testid="checkup-intro">
      <motion.div variants={fadeUp}>
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 hover:text-navy transition-colors min-h-[32px]" data-testid="intro-back-to-pathways">
          <ArrowLeft className="w-3.5 h-3.5" /> All pathways
        </Link>
      </motion.div>
      <motion.div variants={fadeUp} className="mt-5 flex items-center gap-3">
        <span className={`pw-${pathway.key} pw-chip w-11 h-11 rounded-full flex items-center justify-center shrink-0 ring-2 ring-[#D6BB7C]/70 shadow-md`}>
          <Icon className="w-5 h-5 text-[#F6F1E7]" strokeWidth={2.2} />
        </span>
        <p className="text-xs font-bold uppercase tracking-[0.32em] text-gold">{intro.eyebrow}</p>
      </motion.div>
      <motion.div variants={fadeUp} className="hairline-gold w-24 mt-4 mb-5" />
      <motion.h1 variants={fadeUp} className="font-serif text-[34px] sm:text-[40px] lg:text-5xl font-semibold text-navy leading-[1.15] tracking-tight">
        {intro.headline[0]} <br className="sm:hidden" />{intro.headline[1]}
      </motion.h1>
      <motion.p variants={fadeUp} className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">{intro.text}</motion.p>
      <motion.button
        variants={fadeUp}
        onClick={onStart}
        data-testid="checkup-start-button"
        className="mt-6 w-full min-h-[56px] rounded-full bg-[#D6BB7C] hover:bg-[#C9AB66] text-navy font-extrabold text-base tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2 px-4 text-center"
      >
        {intro.cta} <ArrowRight className="w-5 h-5 shrink-0" />
      </motion.button>
      <motion.p variants={fadeUp} className="mt-3 text-center text-xs font-semibold tracking-widest uppercase text-slate-400" data-testid="intro-reassurance-line">
        Complimentary • Educational • No obligation
      </motion.p>
      <motion.div variants={fadeUp} className="mt-6 rounded-2xl border border-sand bg-white px-4 py-4 grid grid-cols-1 min-[400px]:grid-cols-2 gap-x-4 gap-y-2.5" data-testid="intro-feature-list">
        {features.map(({ icon: FIcon, text }) => (
          <div key={text} className="flex items-center gap-2.5">
            <FIcon className="w-4 h-4 text-gold shrink-0" strokeWidth={2.2} />
            <span className="text-sm font-medium text-navy leading-snug">{text}</span>
          </div>
        ))}
      </motion.div>
    </motion.div>
  );
}
